import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef } from 'react';

/**
 * Minimal getUserMedia-based camera preview + frame capture.
 *
 * Built for the no-login pilot verification page (pages/pilotverify.jsx) — there was no
 * existing camera-capture precedent anywhere in this codebase to follow, so this is new,
 * plain browser APIs (no dependency added). Kept as its own component so the real,
 * post-login applicant capture flow can reuse it later instead of duplicating this.
 *
 * Note on mirroring: the <video> preview is CSS-mirrored (scaleX(-1)) purely so it *looks*
 * like a normal selfie/mirror to the person using it — that's a display-only CSS transform.
 * canvas.drawImage() below reads from the underlying video element's actual frame data, which
 * is NOT affected by a CSS transform on the element, so captured frames are the raw,
 * non-mirrored camera feed. This matches the backend's LandmarkLivenessService.MirrorFrames
 * flag, which currently assumes frames are NOT mirrored — if liveness turn-detection comes
 * back backwards during testing, that backend flag (not this component) is what to flip.
 *
 * Face guide overlay (`showFaceGuide`, default on): a purely cosmetic SVG oval "cutout" drawn
 * on top of the video preview, darkening everything outside it — the familiar "fit your face in
 * the oval" look from banking-app ID capture screens. Display-only, like the mirroring above:
 * it does not crop, mask, or otherwise change what captureFrame()/captureBurst() actually send
 * to the backend, which still gets the full raw frame exactly as before. Purely to make the
 * capture screen feel more guided/professional per explicit product feedback.
 */
const CameraCapture = forwardRef(function CameraCapture({ showFaceGuide = true, size = 320 }, ref) {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const [error, setError] = useState('');
    const [ready, setReady] = useState(false);
    const maskId = React.useId();

    useEffect(() => {
        let cancelled = false;

        const start = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'user', width: { ideal: 480 }, height: { ideal: 480 } },
                    audio: false,
                });
                if (cancelled) {
                    stream.getTracks().forEach((t) => t.stop());
                    return;
                }
                streamRef.current = stream;
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    await videoRef.current.play();
                }
                setReady(true);
            } catch (e) {
                setError('Could not access the camera. Please allow camera permission for this page and reload.');
            }
        };

        start();

        return () => {
            cancelled = true;
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
            }
        };
    }, []);

    const captureFrame = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas || !video.videoWidth) return null;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        return canvas.toDataURL('image/jpeg', 0.85);
    };

    /** Captures `count` frames spaced `intervalMs` apart — used for the liveness challenge window. */
    const captureBurst = (count, intervalMs) => {
        return new Promise((resolve) => {
            const frames = [];
            let taken = 0;
            const timer = setInterval(() => {
                const f = captureFrame();
                if (f) frames.push(f);
                taken += 1;
                if (taken >= count) {
                    clearInterval(timer);
                    resolve(frames);
                }
            }, intervalMs);
        });
    };

    useImperativeHandle(ref, () => ({ captureFrame, captureBurst, isReady: () => ready }));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ position: 'relative', width: size, height: size, borderRadius: 8, overflow: 'hidden', backgroundColor: '#000' }}>
                <video
                    ref={videoRef}
                    muted
                    playsInline
                    style={{
                        width: size,
                        height: size,
                        objectFit: 'cover',
                        display: 'block',
                        transform: 'scaleX(-1)',
                    }}
                />
                {showFaceGuide && ready && !error && (
                    <svg
                        width={size}
                        height={size}
                        viewBox={`0 0 ${size} ${size}`}
                        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
                    >
                        <defs>
                            <mask id={maskId}>
                                <rect x="0" y="0" width={size} height={size} fill="white" />
                                <ellipse cx={size / 2} cy={size * 0.52} rx={size * 0.3} ry={size * 0.4} fill="black" />
                            </mask>
                        </defs>
                        <rect x="0" y="0" width={size} height={size} fill="rgba(0,0,0,0.4)" mask={`url(#${maskId})`} />
                        <ellipse
                            cx={size / 2}
                            cy={size * 0.52}
                            rx={size * 0.3}
                            ry={size * 0.4}
                            fill="none"
                            stroke="rgba(255,255,255,0.9)"
                            strokeWidth="2.5"
                            strokeDasharray="7 6"
                        />
                    </svg>
                )}
            </div>
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            {!ready && !error && <div style={{ marginTop: 8 }}>Starting camera...</div>}
            {error && <div style={{ marginTop: 8, color: 'red', textAlign: 'center', maxWidth: size }}>{error}</div>}
        </div>
    );
});

export default CameraCapture;
